import React, { useEffect, useState, useCallback } from 'react';
import { Grid, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import SectionCard from './SectionCard';

const PERIODS = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
];

// PASSKEY, TOTP and IN_APP are the only three authentication methods the
// product offers — the backend never returns anything else.
const METHOD_COLORS = { PASSKEY: '#2e7d32', IN_APP: '#1565c0', TOTP: '#f9a825' };
const METHOD_LABELS = { IN_APP: 'IN-APP' };
const methodLabel = (method) => METHOD_LABELS[method] || method;

const STATUS_COLORS = {
  APPROVED: '#2e7d32',
  TIMEOUT: '#f9a825',
  REJECTED: '#1565c0',
  FAILED: '#c62828',
};

function formatBucketLabel(dateStr, bucket) {
  const d = new Date(dateStr);
  if (bucket === 'hour') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * fetchVolume(period) -> { period, bucket, series: [{date, requests, success, failed}] }
 * fetchMethods(period) -> [{ method, count, percentage }]
 * fetchStatus(period) -> [{ status, count }]
 */
const AuthAnalyticsSection = ({ fetchVolume, fetchMethods, fetchStatus, reloadKey }) => {
  const [period, setPeriod] = useState('7d');
  const [volume, setVolume] = useState(null);
  const [methods, setMethods] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState({ volume: true, methods: true, status: true });
  const [error, setError] = useState({ volume: null, methods: null, status: null });

  const load = useCallback(() => {
    setLoading({ volume: true, methods: true, status: true });
    setError({ volume: null, methods: null, status: null });

    fetchVolume(period)
      .then((res) => setVolume(res.data))
      .catch((e) => setError((prev) => ({ ...prev, volume: e.message })))
      .finally(() => setLoading((prev) => ({ ...prev, volume: false })));

    fetchMethods(period)
      .then((res) => setMethods(res.data))
      .catch((e) => setError((prev) => ({ ...prev, methods: e.message })))
      .finally(() => setLoading((prev) => ({ ...prev, methods: false })));

    fetchStatus(period)
      .then((res) => setStatus(res.data))
      .catch((e) => setError((prev) => ({ ...prev, status: e.message })))
      .finally(() => setLoading((prev) => ({ ...prev, status: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = (volume?.series || []).map((p) => ({
    label: formatBucketLabel(p.date, volume.bucket),
    requests: p.requests,
    success: p.success,
    failed: p.failed,
  }));

  // PASSKEY, TOTP and IN_APP are the only valid methods — the backend
  // already only returns these, but filter defensively in case a stale
  // cached response still has an older value (e.g. PASSWORD) included.
  const VISIBLE_METHODS = ['PASSKEY', 'TOTP', 'IN_APP'];
  const visibleMethods = (methods || [])
    .filter((m) => VISIBLE_METHODS.includes(m.method))
    .map((m) => ({ ...m, displayMethod: methodLabel(m.method) }));

  // This dashboard only reports Approved/Pending/Rejected/Timeout — the backend
  // already excludes everything else, but filter defensively in case a stale
  // cached response still has an older status included.
  const VISIBLE_STATUSES = ['APPROVED', 'FAILED', 'REJECTED', 'TIMEOUT'];
  const visibleStatus = (status || []).filter((s) => VISIBLE_STATUSES.includes(s.status));

  const periodSelector = (
    <ToggleButtonGroup size="small" value={period} exclusive onChange={(e, v) => v && setPeriod(v)}>
      {PERIODS.map((p) => (
        <ToggleButton key={p.value} value={p.value}>
          {p.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SectionCard
          title="Authentication Volume"
          controls={periodSelector}
          loading={loading.volume}
          error={error.volume}
          isEmpty={!loading.volume && !error.volume && chartData.length === 0}
          emptyMessage="No authentication transactions recorded for this period."
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area type="monotone" dataKey="requests" stroke="#1565c0" fill="#1565c033" name="Requests" />
              <Area type="monotone" dataKey="success" stroke="#2e7d32" fill="#2e7d3222" name="Success" />
              <Area type="monotone" dataKey="failed" stroke="#c62828" fill="#c6282822" name="Failed" />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <SectionCard
          title="Authentication Methods"
          loading={loading.methods}
          error={error.methods}
          isEmpty={!loading.methods && !error.methods && visibleMethods.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={visibleMethods} dataKey="count" nameKey="displayMethod" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {visibleMethods.map((m) => (
                  <Cell key={m.method} fill={METHOD_COLORS[m.method] || '#757575'} />
                ))}
              </Pie>
              <Legend />
              <Tooltip formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, props.payload.displayMethod]} />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <SectionCard
          title="Authentication Status"
          loading={loading.status}
          error={error.status}
          isEmpty={!loading.status && !error.status && visibleStatus.length === 0}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={visibleStatus} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="status" tick={{ fontSize: 12 }} width={90} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {visibleStatus.map((s) => (
                  <Cell key={s.status} fill={STATUS_COLORS[s.status] || '#757575'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

export default AuthAnalyticsSection;
